import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Search, Heart, ArrowRight, Play, Shield, Globe, Users, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../ui/input';
import { recentDonations } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import apiClient from '../../services/api';

const HeroSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentDonation, setCurrentDonation] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredCampaigns, setFeaturedCampaigns] = useState([]);
  const [currentCampaignIndex, setCurrentCampaignIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Fetch campaigns for hero rotation
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await apiClient.get('/campaigns?limit=10&status=active');
        // Handle both array and object responses
        const campaigns = Array.isArray(response.data) ? response.data : (response.data.campaigns || []);
        if (campaigns.length > 0) {
          setFeaturedCampaigns(campaigns);
        }
      } catch (err) {
        console.log('Using default hero campaign');
      }
    };
    fetchCampaigns();
  }, []);

  // Auto-rotate through campaigns every 5 seconds
  useEffect(() => {
    if (featuredCampaigns.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentCampaignIndex((prev) => (prev + 1) % featuredCampaigns.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [featuredCampaigns.length]);

  // Donation ticker rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDonation((prev) => (prev + 1) % recentDonations.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const donation = recentDonations[currentDonation];
  const currentCampaign = featuredCampaigns[currentCampaignIndex];

  const handleCampaignClick = () => {
    if (currentCampaign?.id) {
      navigate(`/campaign/${currentCampaign.id}`);
    }
  };

  const goToPrevCampaign = (e) => {
    e.stopPropagation();
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentCampaignIndex((prev) => (prev - 1 + featuredCampaigns.length) % featuredCampaigns.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToNextCampaign = (e) => {
    e.stopPropagation();
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentCampaignIndex((prev) => (prev + 1) % featuredCampaigns.length);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full opacity-50 blur-3xl" />
        <div className="absolute top-60 -left-20 w-60 h-60 bg-accent/20 rounded-full opacity-50 blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-40 h-40 bg-primary/10 rounded-full opacity-50 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            {/* Live Donation Ticker */}
            <div className="inline-flex items-center px-4 py-2 bg-card rounded-full shadow-md border border-border animate-pulse">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
              <span className="text-sm text-muted-foreground">
                <span className="font-semibold text-primary">${donation.amount}</span> donated to "{donation.campaign}" {donation.time}
              </span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                {t('heroTitle')}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-500">
                  {t('heroTitleHighlight')}
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-lg">
                {t('heroSubtitle')}
              </p>
            </div>

            {/* Search Bar */}
            <form 
              className="flex flex-col sm:flex-row gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                } else {
                  navigate('/search');
                }
              }}
            >
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg rounded-xl bg-card shadow-lg"
                />
              </div>
              <Button type="submit" size="lg" className="h-full px-8 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all">
                {t('search')}
              </Button>
            </form>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to="/create">
                <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
                  {t('startCampaign')} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/discover?all=true">
                <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-emerald-500 hover:from-emerald-600 hover:to-emerald-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
                  {t('seeAllCampaigns')} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg rounded-xl border-2 border-border hover:bg-secondary text-foreground">
                  <Play className="mr-2 w-5 h-5" /> {t('seeHowItWorks')}
                </Button>
              </Link>
            </div>

          </div>

          {/* Right Column - Visual */}
          <div className="relative hidden lg:block">
            {/* Main Image Card - Clickable & Auto-rotating */}
            <div className="relative z-10">
              <div 
                onClick={handleCampaignClick}
                className={`bg-card rounded-3xl shadow-2xl overflow-hidden transform rotate-2 hover:rotate-0 transition-all duration-500 border border-border cursor-pointer hover:shadow-emerald-500/20 hover:border-emerald-500/50 ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
              >
                <img
                  src={currentCampaign?.image || currentCampaign?.images?.[0] || "https://images.unsplash.com/photo-1593113598332-cd288d649433?w=600&h=400&fit=crop"}
                  alt={currentCampaign?.title || "Featured campaign"}
                  className="w-full h-72 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-emerald-400 bg-emerald-500/20 px-3 py-1 rounded-full capitalize">
                      {currentCampaign?.category || 'Medical'}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {currentCampaign?.days_left || currentCampaign?.daysLeft || 5} days left
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground mb-2 line-clamp-1">
                    {currentCampaign?.title || "Help Maya's Heart Surgery"}
                  </h3>
                  <div className="w-full bg-secondary rounded-full h-2 mb-2">
                    <div 
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full transition-all duration-500" 
                      style={{ width: `${Math.min(((currentCampaign?.current_amount || currentCampaign?.raised || 78500) / (currentCampaign?.goal || 100000)) * 100, 100)}%` }} 
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold text-emerald-400">
                      ${(currentCampaign?.current_amount || currentCampaign?.raised || 78500).toLocaleString()} raised
                    </span>
                    <span className="text-muted-foreground">
                      of ${(currentCampaign?.goal || 100000).toLocaleString()}
                    </span>
                  </div>
                </div>
                
                {/* Navigation Arrows */}
                {featuredCampaigns.length > 1 && (
                  <>
                    <button 
                      onClick={goToPrevCampaign}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={goToNextCampaign}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              
              {/* Campaign Indicator Dots */}
              {featuredCampaigns.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {featuredCampaigns.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setIsTransitioning(true);
                        setTimeout(() => {
                          setCurrentCampaignIndex(idx);
                          setIsTransitioning(false);
                        }, 300);
                      }}
                      className={`w-2 h-2 rounded-full transition-all ${idx === currentCampaignIndex ? 'bg-emerald-500 w-6' : 'bg-gray-500 hover:bg-gray-400'}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Floating Cards */}
            <div className="absolute -top-8 -left-4 bg-card rounded-2xl shadow-xl p-4 transform -rotate-6 animate-float border border-border z-20">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{t('protected100')}</p>
                  <p className="text-xs text-muted-foreground">{t('donorGuarantee')}</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -right-4 bg-card rounded-2xl shadow-xl p-4 transform rotate-3 animate-float-delayed border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{t('countries120')}</p>
                  <p className="text-xs text-muted-foreground">{t('worldwideReach')}</p>
                </div>
              </div>
            </div>

            <div className="absolute top-1/2 -right-12 bg-card rounded-2xl shadow-xl p-4 transform rotate-6 animate-float border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Growing</p>
                  <p className="text-xs text-muted-foreground">Community</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-6deg); }
          50% { transform: translateY(-10px) rotate(-6deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) rotate(3deg); }
          50% { transform: translateY(-10px) rotate(3deg); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 4s ease-in-out infinite 1s;
        }
      `}} />
    </section>
  );
};

export default HeroSection;
